import { Button, Tooltip, Typography } from "@hellocarbo/lasagne";
import { ArrowRight, Check, Hourglass, LucideIcon } from "lucide-react";
import { PropsWithChildren, use } from "react";
import { useTranslation } from "react-i18next";
import { useFetcher } from "react-router";

import { PortalContainerContext } from "@/contexts";
import { ActionKey, OnboardingAction } from "@/types";
import { cn } from "@/utils/classes";

type ActionProps = {
  Icon: LucideIcon;
  title: string;
  actionKey: ActionKey;
  isSkippable?: OnboardingAction["is_skippable"];
  status: OnboardingAction["status"];
  unavailableReason: string;
  href?: string;
  isExternal?: boolean;
};

const STATUS_ICONS = {
  done: Check,
  todo: ArrowRight,
  unavailable: Hourglass,
};

const Action = ({
  Icon,
  title,
  actionKey,
  isSkippable,
  status,
  unavailableReason,
  href,
  isExternal,
}: ActionProps) => {
  const { t } = useTranslation("onboarding");
  const fetcher = useFetcher();

  const isBeingSkipped = fetcher.formData?.get("actionKey") === actionKey;

  const isShownAsDone = status === "done" || isBeingSkipped;

  const markAsDoneHandler = async () => {
    const response = await fetch(
      `/api/client/onboarding/mark_as_done/${actionKey}`,
      {
        method: "POST",
      }
    );

    if (response.ok) {
      window.location.reload();
    } else {
      console.error("Failed to mark action as done");
    }
  };

  const ignoreClickHandler = () => {
    fetcher.submit(
      { title: "Ignore action", actionKey: actionKey },
      { action: "/onboarding", method: "post" }
    );
  };

  return (
    <li>
      <div
        className={cn(
          "gap-md border-grayscale-xx-light px-md py-sm flex items-center rounded-lg border",
          status === "unavailable" ? "bg-grayscale-xx-light" : "shadow-sm"
        )}
      >
        <span
          className={cn(
            "p-2xs rounded-md border",
            status === "todo" ? "border-primary-light" : "border-grayscale"
          )}
        >
          <Icon
            className={cn(
              "size-md",
              status === "todo" ? "text-primary-light" : "text-grayscale"
            )}
          />
        </span>
        <Typography
          weight="semibold"
          className={cn("grow", isShownAsDone && "text-grayscale")}
        >
          {title}
        </Typography>
        {isSkippable && !isShownAsDone && status !== "unavailable" ? (
          <Button
            intent="primary"
            shape="link"
            size="small"
            onClick={ignoreClickHandler}
          >
            {t("actions.skip")}
          </Button>
        ) : null}
        <ButtonWrapper status={status} unavailableReason={unavailableReason}>
          <Button
            as="a"
            href={href}
            disabled={status === "unavailable"}
            intent={isShownAsDone ? "positive" : "primary"}
            shape={isShownAsDone ? "soft" : "solid"}
            Icon={STATUS_ICONS[isShownAsDone ? "done" : status]}
            size="small"
            className={status === "todo" ? "" : "pointer-events-none"}
            shouldOpenNewTab={isExternal}
            onClick={() => {
              if (isExternal) {
                markAsDoneHandler();
              }
            }}
          >
            {status}
          </Button>
        </ButtonWrapper>
      </div>
    </li>
  );
};

function ButtonWrapper({
  status,
  unavailableReason,
  children,
}: PropsWithChildren<{
  status: OnboardingAction["status"];
  unavailableReason: string;
}>) {
  if (status !== "unavailable" || !unavailableReason) {
    return children;
  }

  const { container } = use(PortalContainerContext);
  return (
    <Tooltip
      side="top"
      portalContainer={container as Element}
      content={unavailableReason}
    >
      {children}
    </Tooltip>
  );
}

export default Action;
